import moment from 'moment';
import humaniseDuration from 'humanise-duration';

const Environments = { 
    uat: "https://globalconfig.dev-bluedot.com/", 
    prod: "https://globalconfig.bluedot.io/" 
};

export const truncateString = (string, maxNumOfChars) => {
    if (string.length > maxNumOfChars) {
        return string.substr(0, maxNumOfChars) + "..."
    }

    return string
};

export const formatTime = (time) => {
    const dateDisplayOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    };

    moment.locale("en", {
      calendar: {
        lastDay: "[Yesterday], LT",
        sameDay: "[Today], LT",
        nextDay: "[Tomorrow], LT",
        lastWeek: "[Last] dddd, LT",
        nextWeek: "dddd, LT",
        sameElse: "[]"
      }
    });

    let timeDisplay = moment(time).calendar();

    if (timeDisplay === "") {
        timeDisplay = time.toLocaleString("en-us", dateDisplayOptions);
        let timeDisplaySplit = timeDisplay.split(",");
        return `${timeDisplaySplit[0]} ${timeDisplaySplit[1]} ${timeDisplaySplit[2]}`
    } else {
        let timeDisplaySplit = timeDisplay.split(",");
        return `${timeDisplaySplit[0]} ${timeDisplaySplit[1]}`
    }
};

export const getEnvironmentShort = (long) => {
    for (let key of Object.keys(Environments)) {
        if (Environments[key] === long) return key.toUpperCase();
    }
    return long;
};

export const doZonesMatch = (entryZone, dwellZone) => {
    if (!entryZone || !dwellZone) return false;
    if (entryZone === dwellZone) return true;
    if (entryZone + "_CO" === dwellZone) return true;
    if (dwellZone.endsWith("_CO") && entryZone === dwellZone.slice(0, -3)) return true;
    
    return false;
};

// Return error message when apps (from mobile) return error to TMP backend
export const getErrorMessage = (currentTestInstanceDevice, sdkVersion, osVersion) => {
    const { systemState, error } = currentTestInstanceDevice

    if (systemState.sdkVersion !== sdkVersion && systemState.osVersion !== osVersion) {
        return `Device SDK version and OS version doesn't match the values registered on TMP`
    }

    if (systemState.sdkVersion !== sdkVersion) {
        return `Device SDK version (${systemState.sdkVersion}) doesn't match Test Instance SDK Version (${sdkVersion})` 
    }

    if (systemState.osVersion !== osVersion) {
        return `Device OS version (${systemState.osVersion}) doesn't match TMP Device Os Version (${osVersion})`
    }

    return error
}

export const getMeanRuleDownloads = (testInstance) => {
    if( !testInstance || !testInstance.devices )
        return 0;
    const total = testInstance.devices.map(device => (device.log&&device.log.numRuleDownloads)?device.log.numRuleDownloads:0).reduce((a, b) => a+b, 0)
    return +(total/testInstance.devices.length).toFixed(2); // Note: + is to reconvert back to Number from String.
}

export const getMedianRuleDownloads = (testInstance) => {
    if( !testInstance || !testInstance.devices )
        return 0;
    const sorted = testInstance.devices.map(device => (device.log&&device.log.numRuleDownloads)?device.log.numRuleDownloads:0).sort((a, b) => a - b)
    const middle = Math.floor(sorted.length / 2);

    return sorted[middle];
}

// --- Test instance comparison (BD-8219) ---

const COMPARISON_NOT_SPECIFIED = "—";

// Derive a human readable status for a completed test instance from its
// devices' host/battery readiness, logs and error flags. Mirrors the status
// logic used in the Completed Test Instances overview so the comparison view
// shows the same status a user would see on the analytics list.
export const getTestInstanceStatus = (testInstance) => {
    if (!testInstance || !testInstance.devices) return "";

    let logs = false;
    let ready = true;
    let error = false;

    Object.values(testInstance.devices).forEach((deviceState) => {
        if (typeof deviceState.batteryReady !== "undefined" && !deviceState.batteryReady) {
            ready = false;
        }
        if (typeof deviceState.hostReady !== "undefined" && !deviceState.hostReady) {
            ready = false;
        }
        if (deviceState.logs) {
            logs = true;
        }
        if (deviceState.error) {
            error = true;
        }
    });

    if (error) return "Error";
    if (!ready) return "Cancelled";
    return logs ? "Completed" : "Finished";
};

const formatComparisonDate = (value) => {
    if (!value) return COMPARISON_NOT_SPECIFIED;
    const date = new Date(value);
    if (isNaN(date.getTime())) return COMPARISON_NOT_SPECIFIED;
    return date.toLocaleString();
};

// Build the ordered list of "relevant data" fields for a single test instance,
// used to compare multiple instances side by side (BD-8219). Values are plain
// strings so that differences between instances can be detected and highlighted.
export const getComparisonFields = (testInstance, scenarioName = "", scenarioEnvironment = "") => {
    if (!testInstance) return [];

    // Prefer scenario details embedded on the instance, falling back to the
    // separately fetched scenario name/environment for older instances.
    let name = scenarioName;
    let environment = scenarioEnvironment;
    if (testInstance.scenario) {
        name = testInstance.scenario.name;
        environment = testInstance.scenario.environment;
    }

    let duration = COMPARISON_NOT_SPECIFIED;
    if (testInstance.startTime && testInstance.endTime) {
        const ms = new Date(testInstance.endTime) - new Date(testInstance.startTime);
        if (!isNaN(ms)) duration = humaniseDuration(ms);
    }

    const deviceCount = Array.isArray(testInstance.devices) ? testInstance.devices.length : 0;

    const deviceDPH =
        testInstance.scenario && !isNaN(testInstance.scenario.deviceDPH)
            ? `${testInstance.scenario.deviceDPH}%`
            : "N/A";

    return [
        { key: "status", label: "Status", value: getTestInstanceStatus(testInstance) || COMPARISON_NOT_SPECIFIED },
        { key: "sdkVersion", label: "SDK Version", value: testInstance.sdkVersion || COMPARISON_NOT_SPECIFIED },
        { key: "sdkBuildNumber", label: "Build Number", value: testInstance.sdkBuildNumber || COMPARISON_NOT_SPECIFIED },
        { key: "appVersion", label: "App Version", value: testInstance.appVersion || COMPARISON_NOT_SPECIFIED },
        { key: "mode", label: "Mode", value: testInstance.mode || COMPARISON_NOT_SPECIFIED },
        { key: "scenario", label: "Scenario", value: name || COMPARISON_NOT_SPECIFIED },
        { key: "environment", label: "Environment", value: getEnvironmentShort(environment) || COMPARISON_NOT_SPECIFIED },
        { key: "meanRuleDownloads", label: "Mean Rule Downloads", value: String(getMeanRuleDownloads(testInstance)) },
        { key: "iosBgLocIndicator", label: "iOS BG Loc Indicator", value: `${testInstance.enableBackgroundLocationIndicator}` },
        { key: "androidBgMode", label: "Android BG Mode", value: `${testInstance.enableBGMode}` },
        { key: "deviceDPH", label: "Device DPH", value: deviceDPH },
        { key: "deviceCount", label: "Devices", value: String(deviceCount) },
        { key: "startTime", label: "Start Time", value: formatComparisonDate(testInstance.startTime) },
        { key: "endTime", label: "End Time", value: formatComparisonDate(testInstance.endTime) },
        { key: "duration", label: "Duration", value: duration },
    ];
};

// Grouping / filtering metadata for a compared instance (BD-8220, BD-8221).
// "Test run" is represented by the scenario the instance was run against, which
// is the closest existing concept to a test run in TMP. Kept separate from the
// comparison field rows so grouping/filtering does not depend on row ordering.
export const getComparisonMeta = (testInstance, scenarioName = "") => {
    if (!testInstance) {
        return { sdkVersion: COMPARISON_NOT_SPECIFIED, sdkBuildNumber: COMPARISON_NOT_SPECIFIED, testRun: COMPARISON_NOT_SPECIFIED };
    }

    let scenario = scenarioName;
    if (testInstance.scenario && testInstance.scenario.name) {
        scenario = testInstance.scenario.name;
    }

    return {
        sdkVersion: testInstance.sdkVersion || COMPARISON_NOT_SPECIFIED,
        sdkBuildNumber: testInstance.sdkBuildNumber || COMPARISON_NOT_SPECIFIED,
        testRun: scenario || COMPARISON_NOT_SPECIFIED,
    };
};

// Escape a single CSV cell: wrap in quotes and double any embedded quotes so
// values containing commas, quotes or newlines survive a round-trip.
const escapeCsvCell = (value) => {
    const stringValue = value === undefined || value === null ? "" : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
};

// Build a CSV string for a comparison (BD-8223). One column per instance and
// one row per comparison field, mirroring what is shown on screen so the export
// matches the highlighted table.
export const buildComparisonCsv = (instances = []) => {
    if (!instances.length) return "";

    const fieldRows = instances[0].fields || [];
    const header = ["Field", ...instances.map(instance => instance.name || instance.id)];
    const lines = [header.map(escapeCsvCell).join(",")];

    fieldRows.forEach((row, rowIndex) => {
        const cells = [row.label];
        instances.forEach(instance => {
            const field = instance.fields && instance.fields[rowIndex];
            cells.push(field ? field.value : "");
        });
        lines.push(cells.map(escapeCsvCell).join(","));
    });

    return lines.join("\n");
};

// --- Analytics comparison metrics (BD-8225) ---
//
// Justin (QA) asked for the key numbers from the single-instance Matrix Analysis
// view to be comparable across instances: check-in trigger rate (Hit %), checkout
// trigger rate (Exit %), checkout vs dwell trigger counts, Time-to-Receive /
// Time-to-Trigger responsiveness groups (Early / On Time / Late) and the SDK
// battery drain (SDK DPH). The functions below mirror the exact formulas used in
// components/presentation/MatrixAnalysis.js and MatrixAnalysis/MatrixTimeToTriggerTable.js
// so the comparison shows the same numbers a user would see on that page. All
// returned field values are plain strings so differences can be highlighted.

// A trigger belongs to a device when it references the device by either its
// external identifier or its Mongo _id (mirrors MatrixAnalysis).
const triggerMatchesDevice = (trigger, device) =>
    trigger.deviceId === device.identifier || trigger.deviceId === device._id;

// Overall check-in trigger rate (Hit %), checkout rate (Exit %), number of
// checkout (exit) triggers received and the total single-hit count used as the
// responsiveness denominator. Mirrors the per device/zone counting and the
// rate/exitRate formulas in MatrixAnalysis.js aggregated across all pairs.
export const computeTriggerSummary = (devices = [], zones = [], triggers = [], showSinglePercentage = false) => {
    let totalSuccess = 0;
    let totalPairs = 0;
    let totalSingleHits = 0;
    let expectedExitCount = 0;
    let totalExitCount = 0;

    devices.forEach(device => {
        zones.forEach(zone => {
            const relevant = triggers.filter(
                trigger => triggerMatchesDevice(trigger, device) && trigger.zoneId === zone._id
            );
            const count = relevant.length;
            const single = count === 1 ? 1 : 0;
            const over = count > 1 ? 1 : 0;

            totalSingleHits += single;
            totalPairs += 1;
            totalSuccess += showSinglePercentage ? single : single + over;

            if (typeof zone.name === "string" && zone.name.endsWith("CO")) {
                expectedExitCount += count;
                totalExitCount += relevant.filter(trigger => trigger.didExit).length;
            }
        });
    });

    return {
        hitRate: totalPairs ? (totalSuccess / totalPairs) * 100 : null,
        exitRate: expectedExitCount ? (totalExitCount / expectedExitCount) * 100 : null,
        checkoutCount: totalExitCount,
        totalSingleHits,
    };
};

// The 10-second window with the highest number of entries for each zone, used as
// the reference "expected" trigger time. Identical to calculateZoneWindows in
// MatrixTimeToTriggerTable.js (including the 0.5 bias toward earlier events).
const calculateComparisonZoneWindows = (triggers, zones, metric) => {
    const zoneWindows = {};
    zones.forEach(zone => {
        const timings = triggers
            .filter(trigger => trigger.zoneId === zone._id)
            .map(trigger => new Date(metric === "eventTime" ? trigger.eventTime : trigger.receivedAt).getTime())
            .sort((a, b) => a - b);

        let bestStart;
        let bestWindowCount = -1;
        for (let i = 0; i < timings.length; i++) {
            let windowCount = -i * 0.5;
            const windowStart = timings[i];
            for (let j = i + 1; j < timings.length && timings[j] < windowStart + 10000; j++) {
                windowCount += 1;
            }
            if (windowCount > bestWindowCount) {
                bestStart = windowStart;
                bestWindowCount = windowCount;
            }
        }
        zoneWindows[zone._id] = bestStart;
    });
    return zoneWindows;
};

// Responsiveness groups (Early / On Time / Late) for a given timing metric.
// `metric` is "receivedAt" for Time-to-Receive (TTR) or "eventTime" for
// Time-to-Trigger (TTT). Mirrors the per-cell classification and the totals row
// in MatrixTimeToTriggerTable.js (Early < window, On Time within 5s, Late after).
export const computeResponsiveness = (devices = [], zones = [], triggers = [], metric = "receivedAt", totalSingleHits = 0) => {
    const zoneWindows = calculateComparisonZoneWindows(triggers, zones, metric);
    let early = 0;
    let onTime = 0;
    let late = 0;

    zones.forEach(zone => {
        const zoneWindow = zoneWindows[zone._id];
        devices.forEach(device => {
            const list = triggers.filter(
                trigger => triggerMatchesDevice(trigger, device) && trigger.zoneId === zone._id
            );
            if (!list.length) return;

            const triggerTime = new Date(metric === "eventTime" ? list[0].eventTime : list[0].receivedAt).getTime();
            const delta = triggerTime - zoneWindow;
            if (delta < 0) early += 1;
            else if (delta < 5000) onTime += 1;
            else late += 1;
        });
    });

    const percentage = value => (totalSingleHits ? ((value * 100) / totalSingleHits).toFixed(2) : "-");

    return {
        early,
        onTime,
        late,
        earlyPct: percentage(early),
        onTimePct: percentage(onTime),
        latePct: percentage(late),
    };
};

// Average SDK battery drain per hour (SDK DPH) across the instance's devices,
// mirroring the SDK DPH column / average in BatteryStats.
export const computeSdkDph = (devices = [], platform = "Both") => {
    let sum = 0;
    let count = 0;
    devices.forEach(device => {
        if (platform !== "Both" && device.os !== platform) return;
        if (typeof device.sdkDPH === "number" && !isNaN(device.sdkDPH)) {
            sum += device.sdkDPH;
            count += 1;
        }
    });
    return count ? sum / count : null;
};

// Build the ordered analytics comparison rows appended after the base relevant
// data fields. Always returns the same rows in the same order (with "N/A" when
// the underlying analytics could not be loaded) so the comparison table and CSV
// stay aligned across instances. `analytics` is
// { devices, zones, triggers, dwellEvents } or null.
export const getComparisonAnalyticsFields = (analytics) => {
    const formatPercent = value => (value === null || value === undefined ? "N/A" : `${value.toFixed(2)}%`);
    const formatCount = value => (value === null || value === undefined ? "N/A" : String(value));
    const formatGroup = (group, key, pctKey) =>
        group ? `${group[key]} (${group[pctKey]}%)` : "N/A";

    let summary = { hitRate: null, exitRate: null, checkoutCount: null, totalSingleHits: 0 };
    let dwellCount = null;
    let ttr = null;
    let ttt = null;
    let sdkDph = null;

    if (analytics) {
        const devices = analytics.devices || [];
        const zones = analytics.zones || [];
        const triggers = analytics.triggers || [];
        const dwellEvents = analytics.dwellEvents || [];

        summary = computeTriggerSummary(devices, zones, triggers, false);
        dwellCount = dwellEvents.length;
        ttr = computeResponsiveness(devices, zones, triggers, "receivedAt", summary.totalSingleHits);
        ttt = computeResponsiveness(devices, zones, triggers, "eventTime", summary.totalSingleHits);
        sdkDph = computeSdkDph(devices, "Both");
    }

    return [
        { key: "hitRate", label: "Hit % (Checkins)", value: formatPercent(summary.hitRate) },
        { key: "exitRate", label: "Exit % (Checkouts)", value: formatPercent(summary.exitRate) },
        { key: "checkoutTriggers", label: "Checkout Triggers", value: formatCount(summary.checkoutCount) },
        { key: "dwellTriggers", label: "Dwell Triggers", value: formatCount(dwellCount) },
        { key: "ttrEarly", label: "TTR — Early", value: formatGroup(ttr, "early", "earlyPct") },
        { key: "ttrOnTime", label: "TTR — On Time (Responsiveness)", value: formatGroup(ttr, "onTime", "onTimePct") },
        { key: "ttrLate", label: "TTR — Late", value: formatGroup(ttr, "late", "latePct") },
        { key: "tttEarly", label: "TTT — Early", value: formatGroup(ttt, "early", "earlyPct") },
        { key: "tttOnTime", label: "TTT — On Time (Responsiveness)", value: formatGroup(ttt, "onTime", "onTimePct") },
        { key: "tttLate", label: "TTT — Late", value: formatGroup(ttt, "late", "latePct") },
        { key: "sdkDph", label: "SDK DPH (Battery Drain)", value: sdkDph === null ? "N/A" : sdkDph.toFixed(2) },
    ];
};
